import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  OnInit,
  Output,
  Self,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import {
  PaperlessPreferenceFieldsetComponent,
  ProtectionStartSummaryPanelComponent,
  TermsAndConditionsFieldsetComponent,
} from '@checkout/ui-components';
import { CheckoutFeature, PaysafeFormValue } from '@checkout/util-models';
import { HasSelectedProductTypeDirective } from '@domgen/dgx-fe-basket';
import { ProductType } from '@domgen/dgx-fe-common';
import { fadeOut } from '@domgen/dgx-fe-components/animation';
import {
  AddressToArrayPipe,
  TestHookDirective,
} from '@domgen/dgx-fe-components/general';
import { LoadingOverlayComponent } from '@domgen/dgx-fe-components/loading';
import { FeatureEnabledDirective } from '@domgen/dgx-fe-config';
import { AdditionalAddressComponent } from '@domgen/dgx-fe-forms/address';
import { CheckboxComponent } from '@domgen/dgx-fe-forms/checkbox';
import { TextInputComponent } from '@domgen/dgx-fe-forms/input';
import {
  FormFieldComponent,
  FormFieldDirective,
  FormFieldsetComponent,
  FormFieldsetDirective,
  FormRowComponent,
  FormSubmitButtonComponent,
} from '@domgen/dgx-fe-forms/layout';
import {
  FormDisabledDirective,
  FormSubmitDirective,
} from '@domgen/dgx-fe-forms/shared';
import {
  GAContainerDirective,
  GAFormFieldDirective,
  GAFormSubmitDirective,
} from '@domgen/dgx-fe-ga';
import { TranslatePipe } from '@domgen/dgx-fe-i18n';
import { QMSanitiseDirective } from '@domgen/dgx-fe-qm';
import { first } from 'rxjs';
import { PaysafeTestHooks } from '../../models/test/paysafe-test-hooks.enum';
import { PaysafeFormComponentService } from '../../services/paysafe-form-component/paysafe-form-component.service';
import { PaysafeFormFieldComponent } from '../paysafe-form-field/paysafe-form-field.component';

@Component({
  selector: 'checkout-paysafe-form',
  templateUrl: './paysafe-form.component.html',
  styleUrls: ['./paysafe-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeOut],
  providers: [PaysafeFormComponentService],
  standalone: true,
  imports: [
    AdditionalAddressComponent,
    AddressToArrayPipe,
    AsyncPipe,
    CheckboxComponent,
    FeatureEnabledDirective,
    FormDisabledDirective,
    FormFieldComponent,
    FormFieldDirective,
    FormFieldsetComponent,
    FormFieldsetDirective,
    FormRowComponent,
    FormSubmitButtonComponent,
    FormSubmitDirective,
    GAContainerDirective,
    GAFormFieldDirective,
    GAFormSubmitDirective,
    HasSelectedProductTypeDirective,
    LoadingOverlayComponent,
    NgFor,
    NgIf,
    PaperlessPreferenceFieldsetComponent,
    PaysafeFormFieldComponent,
    ProtectionStartSummaryPanelComponent,
    QMSanitiseDirective,
    ReactiveFormsModule,
    TermsAndConditionsFieldsetComponent,
    TestHookDirective,
    TextInputComponent,
    TranslatePipe,
  ],
})
export class PaysafeFormComponent implements AfterViewInit, OnInit {
  @Output() submitted = new EventEmitter<string>();

  productType = ProductType;
  termsAndConditionsFeature = CheckoutFeature.TermsAndConditions;
  testHooks = PaysafeTestHooks;

  constructor(
    @Self() readonly cs: PaysafeFormComponentService,
    private readonly destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.cs.buildForm();
  }

  ngAfterViewInit(): void {
    this.cs.setup();
  }

  onFormSubmit(formValue: PaysafeFormValue): void {
    this.cs
      .tokenizeFormValue(formValue)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe((cardPaymentReference: string) => {
        this.submitted.emit(cardPaymentReference);
      });
  }
}
